import type { MoneyAmount } from '@optiflow/types';
import { formatMoney } from '@/modules/catalog/money';
import { useLocale } from '@/hooks/useLocale';
import { cn } from '@/utils/cn';

/**
 * Amounts in every currency present, one per line, base currency first.
 *
 * Never a single merged total: EGP and AED are two facts, and adding them would print a number
 * the data does not support. Each amount is isolated left-to-right so its sign and digits never
 * reorder inside an Arabic sentence.
 */
export function MoneyByCurrencyText({
  amounts,
  className,
  secondaryClassName,
}: {
  amounts: MoneyAmount[];
  className?: string;
  secondaryClassName?: string;
}) {
  const { locale } = useLocale();
  const [primary, ...others] = amounts;
  if (!primary) return null;

  return (
    <span className="flex flex-col">
      <span className={className} dir="ltr">
        {formatMoney(primary.amountMinor, locale, primary.currencyCode)}
      </span>
      {others.map((entry) => (
        <span
          key={entry.currencyCode}
          className={cn('text-muted text-sm', secondaryClassName)}
          dir="ltr"
        >
          {formatMoney(entry.amountMinor, locale, entry.currencyCode)}
        </span>
      ))}
    </span>
  );
}
